import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { Heading, Text } from 'react-aria-components';
import PageTitle from 'src/components/creator/PageTitle';
import Button from 'src/components/ui/Button';
import PageLoader from 'src/components/ui/PageLoader';
import RouterButton from 'src/components/ui/RouterButton';
import Modal from 'src/components/ui/Modal';
import ConfirmationDialog from 'src/components/ui/ConfirmationDialog';
import RequestOrderForm, {
  getStorageKey,
  computeDefaultValues,
  filterSavedValues,
  deepMerge,
  readFormValues,
  clearFormValues,
  DEFAULT_VALUES,
} from 'src/components/creator/RequestOrderForm';
import CheckIcon from 'src/assets/icons/shield-check.svg';
import DownloadBottom from 'src/assets/icons/download-bottom.svg';
import { RequestOrderSchemaType } from 'src/schema/order-request';
import { DEFAULT_CITATION } from 'src/schema/citation';
import PUBLICATION_QUERY from 'src/graphql/query/genie-publication.graphql';
import useUser from 'src/hooks/useUser';

import { RequestMaterialType, RequestOwnMaterials } from 'src/types/order';

import type { Publication } from 'src/types/publication';

const CreateOrderRequestPage = () => {
  const { publicationId } = Route.useSearch();
  const [successData, setSuccessData] =
    useState<Partial<RequestOrderSchemaType> | null>(null);

  const {
    loading,
    initialValues,
    filteredSaved,
    storageKey,
    isReady,
    restorePromptOpen,
    handleRestore,
    handleStartFresh,
  } = useCreateOrderInit(publicationId);

  if (loading) return <PageLoader />;

  if (successData)
    return (
      <Container>
        <SuccessScreen
          data={successData}
          onRestart={() => setSuccessData(null)}
        />
      </Container>
    );

  return (
    <>
      <RestorePromptDialog
        publicationId={publicationId}
        restorePromptOpen={restorePromptOpen}
        filteredSaved={filteredSaved}
        handleRestore={handleRestore}
        handleStartFresh={handleStartFresh}
      />

      {isReady ? (
        <RequestOrderForm
          storageKey={storageKey}
          initialValues={initialValues}
          onSuccess={(r) => setSuccessData(r)}
        />
      ) : (
        <PageLoader />
      )}
    </>
  );
};

const Container = ({ children }: { children: ReactNode }) => (
  <>
    <PageTitle title="Create Order" />
    <div className="relative overflow-hidden bg-surface-alt rounded-xl shadow-3 mt-4 p-6 flex-1 flex items-center justify-center">
      {children}
    </div>
  </>
);

const SuccessScreen = ({
  data,
  onRestart,
}: {
  data: Partial<RequestOrderSchemaType>;
  onRestart: () => void;
}) => {
  return (
    <div className="max-w-98 text-center text-base font-medium text-surface-alt-90">
      <CheckIcon className="inline-block w-20 h-20" />
      <Heading level={5} className="text-2xl font-bold mt-9 mb-4">
        Thank you for your order!
      </Heading>
      <Text elementType="p">
        It has been successfully submitted and is currently being processed.
        Your account manager will be in touch soon.
      </Text>

      <hr className="my-7 border-surface-10" />

      <Text
        elementType="strong"
        className="text-surface-alt-90 text-lg font-bold"
      >
        {data.courseDetails?.name ?? ''}
      </Text>
      <Text
        elementType="span"
        className="block my-0.5 text-surface-alt-75 text-md font-semibold"
      >
        {data.courseDetails?.number ?? ''}
      </Text>
      <Text
        elementType="span"
        className="text-surface-alt-90 text-md font-normal"
      >
        {data.creatorDetails?.professorIds.map((p) => p.label).join(', ')}
      </Text>

      <div className="bg-surface-alt absolute bottom-0 left-0 right-0 flex items-center justify-end gap-2.5 p-4 pt-3">
        <RouterButton
          to="/requests"
          buttonProps={{ variant: 'surface', ghost: true }}
        >
          Go to Order Requests
        </RouterButton>
        <Button onPress={onRestart} variant="secondary">
          Request New Order
        </Button>
      </div>
    </div>
  );
};

interface RestorePromptDialogProps {
  restorePromptOpen: boolean;
  filteredSaved: Record<string, unknown> | undefined;
  handleRestore: () => Promise<void>;
  handleStartFresh: () => Promise<void>;
  publicationId?: string;
}

const RestorePromptDialog = ({
  publicationId,
  restorePromptOpen,
  handleRestore,
  handleStartFresh,
  filteredSaved,
}: RestorePromptDialogProps) => {
  const dialogContent = useMemo(() => {
    if (publicationId) {
      return {
        title: 'Resume previous session?',
        message:
          'You have an unsaved draft. You can restore it, or discard it to start your new publication order.',
        confirmBtn: 'Restore Draft',
        cancelBtn: 'Discard & Start New',
      };
    }
    return {
      title: 'Restore your work?',
      message:
        'We found unsaved changes from a previous session. Restore them now?',
      confirmBtn: 'Restore',
      cancelBtn: 'Start Fresh',
    };
  }, [publicationId]);

  return (
    <Modal
      backdropBlur={false}
      isOpen={restorePromptOpen}
      className="max-w-[31.25rem]"
    >
      <ConfirmationDialog
        title={dialogContent.title}
        buttonTitle={dialogContent.confirmBtn}
        cancelTitle={dialogContent.cancelBtn}
        image={null}
        buttonProps={{ IconLeft: DownloadBottom, variant: 'success' }}
        onConfirm={handleRestore}
        onCancel={handleStartFresh}
      >
        {dialogContent.message}

        {filteredSaved && Object.keys(filteredSaved).length > 0 && (
          <section>
            <hr className="my-6 border-surface-10" />
            <div className="text-sm text-surface-alt-75 font-medium mb-2">
              The following data will be restored:
            </div>
            <ul className="list-disc list-inside text-sm text-surface-alt-75">
              {Object.keys(filteredSaved).map((key) => (
                // de-camelcase keys for better readability
                <li key={key}>
                  {key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, (str) => str.toUpperCase())}
                </li>
              ))}
            </ul>
          </section>
        )}
      </ConfirmationDialog>
    </Modal>
  );
};

/**
 * Handles initialization for create order flow:
 * - Computes storage key
 * - Checks localStorage for saved data
 * - Prompts user to restore if meaningful data exists
 * - Handles publication-specific overrides
 */
const useCreateOrderInit = (publicationId?: string) => {
  const { user, loading: userLoading } = useUser();
  const [isReady, setIsReady] = useState(false);
  const [initialValues, setInitialValues] =
    useState<Partial<typeof DEFAULT_VALUES>>();
  const [restorePromptOpen, setRestorePromptOpen] = useState(false);
  const [pendingRestore, setPendingRestore] = useState<{
    effectiveDefaults: Partial<typeof DEFAULT_VALUES>;
    filteredSaved: Record<string, unknown>;
  } | null>(null);

  const { data, loading: pubLoading } = useQuery<{ publication: Publication }>(
    PUBLICATION_QUERY,
    {
      variables: { publicationId: Number(publicationId) },
      skip: !publicationId,
    }
  );

  const storageKey = user.clientId ? getStorageKey(String(user.clientId)) : '';

  const baseDefaults = useMemo(
    () => (user.clientId ? computeDefaultValues(user) : null),
    [user]
  );

  const effectiveDefaults = useMemo(() => {
    if (!baseDefaults) return null;

    return publicationId
      ? applyPublicationOverrides(baseDefaults, data?.publication)
      : baseDefaults;
  }, [baseDefaults, publicationId, data?.publication]);

  const handleRestore = useCallback(async () => {
    if (!pendingRestore) return;

    const finalValues = deepMerge(
      pendingRestore.effectiveDefaults,
      pendingRestore.filteredSaved
    );

    setInitialValues(finalValues);
    setIsReady(true);
    setRestorePromptOpen(false);
    setPendingRestore(null);
  }, [pendingRestore]);

  const handleStartFresh = useCallback(async () => {
    if (!pendingRestore) return;

    setInitialValues(pendingRestore.effectiveDefaults);
    setIsReady(true);
    setRestorePromptOpen(false);
    clearFormValues(storageKey);
    setPendingRestore(null);
  }, [pendingRestore, storageKey]);

  // Handle localStorage restore prompt
  useEffect(() => {
    if (userLoading || !user.clientId) return;
    if (publicationId && pubLoading) return;
    if (isReady || restorePromptOpen) return;
    if (!effectiveDefaults) return;

    const savedData = readFormValues(storageKey);
    const filteredSaved = savedData
      ? filterSavedValues(savedData, effectiveDefaults)
      : undefined;

    const savedKeys = Object.keys(filteredSaved || []);
    const isPublicationReady =
      publicationId &&
      savedKeys.length === 2 &&
      savedKeys.includes('materialsData');

    if (!filteredSaved || isPublicationReady) {
      setInitialValues(effectiveDefaults);
      setIsReady(true);
      return;
    }

    setPendingRestore({
      effectiveDefaults,
      filteredSaved,
    });
    setRestorePromptOpen(true);
  }, [
    userLoading,
    user.clientId,
    publicationId,
    pubLoading,
    isReady,
    restorePromptOpen,
    effectiveDefaults,
    storageKey,
  ]);

  return {
    loading: userLoading || pubLoading,
    initialValues,
    filteredSaved: pendingRestore?.filteredSaved,
    storageKey,
    isReady,
    restorePromptOpen,
    handleRestore,
    handleStartFresh,
  };
};

/**
 * Applies publication-specific overrides to initial values
 */
const applyPublicationOverrides = (
  baseValues: Partial<typeof DEFAULT_VALUES>,
  publication?: Publication
) => {
  if (!publication) return baseValues;

  const material = {
    id: `m-p${publication.publicationId}`,
    publicationId: String(publication.publicationId),
    mailinCode: null,
    materialType: RequestMaterialType.REQUEST,
    requestMaterialInstructions: '',
    title: publication.publicationTitle,
    uploadFileId: null,
    uploadFilename: null,
    citations: [],
  };

  const citation = {
    ...DEFAULT_CITATION,
    author: publication.authors
      .map((a) => `${a.firstName} ${a.lastName}`)
      .join(', '),
    citationTitle: `${publication.publicationTitle}`.concat(
      publication.edition ? ` (${publication.edition})` : ''
    ),
    isbn: publication.isbnIssn,
    outOfPrint: true,
    entireBook: true,
  };

  return {
    ...baseValues,
    materialsData: {
      materials: [{ ...material, citations: [citation] }],
    },
    copyrights: {
      ownMaterial: RequestOwnMaterials.OtherSources,
      ladLibrary: false,
      wantsRoyalties: false,
      royaltiesAmount: '',
    },
  };
};

const CreateRequestSchema = z.object({
  publicationId: z.string().optional(),
});

export const Route = createFileRoute(
  '/_authenticated/_creator/requests/create'
)({
  component: CreateOrderRequestPage,
  validateSearch: (search) => CreateRequestSchema.parse(search),
});
